// @ts-nocheck
/**
 * Course Content Page
 * Course material access interface with Carbon ContentSwitcher
 * Epic 75 - Work Item 3: R2 Object Storage and Content Delivery Architecture
 */

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Grid,
  Column,
  Breadcrumb,
  BreadcrumbItem,
  ContentSwitcher,
  Switch,
  Accordion,
  AccordionItem,
  Button,
  Tag,
  SkeletonText,
  InlineNotification,
  Tile,
  ProgressBar,
} from '@carbon/react';
import {
  Download,
  Play,
  Document,
  Video,
  DocumentPdf,
  Certificate,
  CheckmarkFilled,
  Time,
  View,
} from '@carbon/react/icons';
import { useAuth } from '../hooks/useAuth';

interface CourseModule {
  id: string;
  title: string;
  description: string;
  order: number;
  materials: CourseMaterial[];
}

interface CourseMaterial {
  id: string;
  title: string;
  description?: string;
  type: 'video' | 'document' | 'presentation' | 'quiz' | 'assignment';
  fileId: string;
  fileUrl: string;
  duration?: number; // in minutes for videos
  completed: boolean;
  downloadable: boolean;
  order: number;
}

interface CourseInfo {
  id: string;
  title: string;
  description: string;
  instructor: string;
  modules: CourseModule[];
  progress: number;
  totalMaterials: number;
  completedMaterials: number;
}

const CourseContent: React.FC = () => {
  const { id: courseId } = useParams<{ id: string }>();
  const { user } = useAuth();
  const [courseInfo, setCourseInfo] = useState<CourseInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'modules' | 'list'>('modules');
  const [selectedMaterial, setSelectedMaterial] = useState<CourseMaterial | null>(null);

  const fetchCourseContent = async () => {
    setLoading(true);
    try {
      const response = await fetch(`/api/v1/courses/${courseId}/content`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch course content');
      }

      const data = await response.json();
      setCourseInfo(data);

      // Auto-select first material
      if (data.modules.length > 0 && data.modules[0].materials.length > 0) {
        setSelectedMaterial(data.modules[0].materials[0]);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (courseId) {
      fetchCourseContent();
    }
  }, [courseId]);

  const handleDownload = async (material: CourseMaterial) => {
    try {
      const response = await fetch(`/api/v1/storage/download/${material.fileId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        throw new Error('Download failed');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = material.title;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Download error:', error);
    }
  };

  const markAsCompleted = async (materialId: string) => {
    try {
      await fetch(`/api/v1/courses/${courseId}/materials/${materialId}/complete`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      // Refresh course content
      fetchCourseContent();
    } catch (error) {
      console.error('Error marking as completed:', error);
    }
  };

  const getMaterialIcon = (type: string) => {
    const iconProps = { size: 20 };
    switch (type) {
      case 'video':
        return <Video {...iconProps} />;
      case 'document':
        return <Document {...iconProps} />;
      case 'presentation':
        return <Presentation {...iconProps} />;
      case 'quiz':
        return <Certificate {...iconProps} />;
      default:
        return <Document {...iconProps} />;
    }
  };

  const getMaterialTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      video: 'blue',
      document: 'gray',
      presentation: 'purple',
      quiz: 'green',
      assignment: 'teal',
    };
    return colors[type] || 'gray';
  };

  const getAllMaterials = () => {
    if (!courseInfo) return [];
    return courseInfo.modules.flatMap(module => module.materials);
  };

  if (loading) {
    return (
      <Grid fullWidth>
        <Column lg={16} md={8} sm={4}>
          <SkeletonText paragraph lineCount={10} />
        </Column>
      </Grid>
    );
  }

  if (error || !courseInfo) {
    return (
      <InlineNotification kind="error" title="Error loading course content" subtitle={error || 'Course not found'} />
    );
  }

  return (
    <div className="course-content-page">
      <Grid fullWidth>
        <Column lg={16} md={8} sm={4}>
          {/* Breadcrumb */}
          <Breadcrumb className="mb-4">
            <BreadcrumbItem href="/">Home</BreadcrumbItem>
            <BreadcrumbItem href="/courses">Courses</BreadcrumbItem>
            <BreadcrumbItem isCurrentPage>{courseInfo.title}</BreadcrumbItem>
          </Breadcrumb>

          {/* Course Header */}
          <div className="course-header mb-6">
            <h1 className="text-3xl font-semibold mb-2">{courseInfo.title}</h1>
            <p className="text-gray-600 mb-4">{courseInfo.description}</p>
            <div className="flex items-center gap-4 mb-4">
              <span className="text-sm text-gray-500">Instructor: {courseInfo.instructor}</span>
              <span className="text-sm text-gray-500">•</span>
              <span className="text-sm text-gray-500">{courseInfo.totalMaterials} materials</span>
              <span className="text-sm text-gray-500">•</span>
              <span className="text-sm text-gray-500">{courseInfo.modules.length} modules</span>
            </div>

            {/* Progress Bar */}
            <div className="mb-4">
              <div className="flex justify-between mb-1">
                <span className="text-sm font-medium">Course Progress</span>
                <span className="text-sm text-gray-500">
                  {courseInfo.completedMaterials} of {courseInfo.totalMaterials} completed
                </span>
              </div>
              <ProgressBar value={courseInfo.progress} max={100} size="big" label={`${courseInfo.progress}%`} />
            </div>
          </div>

          <Grid narrow>
            {/* Left Sidebar - Course Materials */}
            <Column lg={5} md={8} sm={4}>
              <div className="mb-4">
                <ContentSwitcher
                  onChange={(e: any) => setViewMode(e.name as 'modules' | 'list')}
                  selectedIndex={viewMode === 'modules' ? 0 : 1}
                >
                  <Switch name="modules" text="Modules" />
                  <Switch name="list" text="All Materials" />
                </ContentSwitcher>
              </div>

              {viewMode === 'modules' ? (
                <Accordion>
                  {courseInfo.modules.map((module, index) => (
                    <AccordionItem
                      key={module.id}
                      title={
                        <div className="flex items-center justify-between w-full">
                          <span>
                            Module {index + 1}: {module.title}
                          </span>
                          <Tag type="gray" size="sm">
                            {module.materials.length} items
                          </Tag>
                        </div>
                      }
                      open={index === 0}
                    >
                      <div className="space-y-2">
                        <p className="text-sm text-gray-600 mb-3">{module.description}</p>
                        {module.materials.map(material => (
                          <Tile
                            key={material.id}
                            className={`cursor-pointer hover:bg-gray-50 ${
                              selectedMaterial?.id === material.id ? 'ring-2 ring-blue-500' : ''
                            }`}
                            onClick={() => setSelectedMaterial(material)}
                          >
                            <div className="flex items-start gap-3">
                              <div className="mt-1">{getMaterialIcon(material.type)}</div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-sm">{material.title}</span>
                                  {material.completed && <CheckmarkFilled className="text-green-500" size={16} />}
                                </div>
                                {material.duration && (
                                  <div className="flex items-center gap-1 mt-1">
                                    <Time size={14} className="text-gray-400" />
                                    <span className="text-xs text-gray-500">{material.duration} min</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </Tile>
                        ))}
                      </div>
                    </AccordionItem>
                  ))}
                </Accordion>
              ) : (
                <div className="space-y-2">
                  {getAllMaterials().map(material => (
                    <Tile
                      key={material.id}
                      className={`cursor-pointer hover:bg-gray-50 ${
                        selectedMaterial?.id === material.id ? 'ring-2 ring-blue-500' : ''
                      }`}
                      onClick={() => setSelectedMaterial(material)}
                    >
                      <div className="flex items-start gap-3">
                        <div className="mt-1">{getMaterialIcon(material.type)}</div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm">{material.title}</span>
                            <Tag type={getMaterialTypeColor(material.type) as any} size="sm">
                              {material.type}
                            </Tag>
                            {material.completed && <CheckmarkFilled className="text-green-500" size={16} />}
                          </div>
                          {material.description && <p className="text-xs text-gray-500 mt-1">{material.description}</p>}
                          {material.duration && (
                            <div className="flex items-center gap-1 mt-1">
                              <Time size={14} className="text-gray-400" />
                              <span className="text-xs text-gray-500">{material.duration} min</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </Tile>
                  ))}
                </div>
              )}
            </Column>

            {/* Right Content - Material Viewer */}
            <Column lg={11} md={8} sm={4}>
              {selectedMaterial ? (
                <div className="material-viewer bg-white rounded-lg shadow-sm p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h2 className="text-2xl font-semibold mb-2">{selectedMaterial.title}</h2>
                      {selectedMaterial.description && <p className="text-gray-600">{selectedMaterial.description}</p>}
                    </div>
                    <div className="flex gap-2">
                      {selectedMaterial.downloadable && (
                        <Button
                          kind="secondary"
                          size="sm"
                          renderIcon={Download}
                          onClick={() => handleDownload(selectedMaterial)}
                        >
                          Download
                        </Button>
                      )}
                      {!selectedMaterial.completed && (
                        <Button
                          kind="primary"
                          size="sm"
                          renderIcon={CheckmarkFilled}
                          onClick={() => markAsCompleted(selectedMaterial.id)}
                        >
                          Mark Complete
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Material Content Area */}
                  <div className="material-content mt-6">
                    {selectedMaterial.type === 'video' ? (
                      <div className="video-container">
                        <video controls className="w-full rounded" src={selectedMaterial.fileUrl}>
                          Your browser does not support the video tag.
                        </video>
                      </div>
                    ) : selectedMaterial.type === 'document' || selectedMaterial.type === 'presentation' ? (
                      <div className="document-viewer">
                        <iframe
                          src={`${selectedMaterial.fileUrl}#toolbar=0`}
                          className="w-full h-96 rounded border"
                          title={selectedMaterial.title}
                        />
                        <div className="mt-4 p-4 bg-gray-50 rounded">
                          <div className="flex items-center gap-2">
                            <View size={20} />
                            <span className="text-sm">
                              Document preview.
                              <Button kind="ghost" size="sm" href={selectedMaterial.fileUrl} target="_blank">
                                Open in new tab
                              </Button>
                            </span>
                          </div>
                        </div>
                      </div>
                    ) : selectedMaterial.type === 'quiz' ? (
                      <div className="quiz-container p-6 bg-blue-50 rounded">
                        <Certificate size={48} className="mb-4 text-blue-600" />
                        <h3 className="text-xl font-semibold mb-2">Quiz Available</h3>
                        <p className="text-gray-600 mb-4">Complete this quiz to test your knowledge of the material.</p>
                        <Button kind="primary" href={selectedMaterial.fileUrl}>
                          Start Quiz
                        </Button>
                      </div>
                    ) : (
                      <div className="assignment-container p-6 bg-gray-50 rounded">
                        <Document size={48} className="mb-4 text-gray-600" />
                        <h3 className="text-xl font-semibold mb-2">Assignment</h3>
                        <p className="text-gray-600 mb-4">Download the assignment instructions and submit your work.</p>
                        <div className="flex gap-2">
                          <Button kind="primary" renderIcon={Download} onClick={() => handleDownload(selectedMaterial)}>
                            Download Instructions
                          </Button>
                          <Button kind="secondary" href="/assignments/submit">
                            Submit Assignment
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-96 bg-gray-50 rounded-lg">
                  <div className="text-center">
                    <Document size={48} className="mx-auto mb-4 text-gray-400" />
                    <p className="text-gray-500">Select a material from the left to view its content</p>
                  </div>
                </div>
              )}
            </Column>
          </Grid>
        </Column>
      </Grid>
    </div>
  );
};

export default CourseContent;
